/**
 * Alarm sound types.
 * SynthTone = Web Audio API oscillator presets
 * WavSound = wav file references in /static/audio/
 */
export const SYNTH_TONES = [
  'classic',
  'bell',
  'chime',
  'pulse',
  'urgent',
] as const
type SynthTone = (typeof SYNTH_TONES)[number]

type WavSound =
  | 'arcade'
  | 'clock-alarm'
  | 'clock-bells'
  | 'cricket'
  | 'emergency'
  | 'kitchen-ding'
  | 'marimba'
  | 'marimba-waiting'
  | 'slot-machine'
  | 'vintage-phone'
  | 'vintage-warning'
  | 'bonus'

export type AlarmSound = SynthTone | WavSound

export function isSynthTone(sound: AlarmSound): sound is SynthTone {
  return (SYNTH_TONES as readonly string[]).includes(sound)
}

interface SoundPreset {
  type: OscillatorType
  frequency: number
  duration: number
}

const SOUND_PRESETS: Record<SynthTone, SoundPreset> = {
  classic: { type: 'sine', frequency: 440, duration: 0.2 },
  bell: { type: 'sine', frequency: 830, duration: 0.35 },
  chime: { type: 'triangle', frequency: 587, duration: 0.3 },
  pulse: { type: 'square', frequency: 330, duration: 0.15 },
  urgent: { type: 'sawtooth', frequency: 520, duration: 0.25 },
}

export const WAV_SOUND_MAP: Record<WavSound, string> = {
  arcade: '/audio/mixkit-arcade-retro-run-sound-220.wav',
  'clock-alarm': '/audio/mixkit-clock-alarm-beep-1081.wav',
  'clock-bells': '/audio/mixkit-clock-bells-hour-signal-1069.wav',
  cricket: '/audio/mixkit-simple-cricket-alarm-tone-2978.wav',
  emergency: '/audio/mixkit-emergency-alert-alarm-1007.wav',
  'kitchen-ding': '/audio/mixkit-kitchen-oven-ding-1838.wav',
  marimba: '/audio/mixkit-marimba-ringtone-1359.wav',
  'marimba-waiting': '/audio/mixkit-marimba-waiting-ringtone-1360.wav',
  'slot-machine': '/audio/mixkit-slot-machine-win-alarm-1995.wav',
  'vintage-phone': '/audio/mixkit-vintage-telephone-ringtone-1356.wav',
  'vintage-warning': '/audio/mixkit-vintage-warning-alarm-990.wav',
  bonus: '/audio/mixkit-winning-an-extra-bonus-2060.wav',
}

/**
 * Playback config for wav sounds.
 * - maxDurationSec: stop playback at this point in the file
 * - repeatCount: number of times to play (default 1)
 * - repeatIntervalMs: ms between repeats (default 500)
 */
interface WavPlaybackConfig {
  maxDurationSec?: number
  repeatCount?: number
  repeatIntervalMs?: number
}

const WAV_PLAYBACK_CONFIG: Partial<Record<WavSound, WavPlaybackConfig>> = {
  'kitchen-ding': { repeatCount: 5, repeatIntervalMs: 500 },
  marimba: { maxDurationSec: 9 },
  'marimba-waiting': { maxDurationSec: 6.8 },
  emergency: { maxDurationSec: 3 },
  'clock-alarm': { maxDurationSec: 4 },
  cricket: { maxDurationSec: 4 },
  'slot-machine': { maxDurationSec: 3.9 },
  'vintage-phone': { maxDurationSec: 4 },
  'vintage-warning': { maxDurationSec: 4 },
}

/** Synth tones repeat 5 times with 0.5s interval */
const SYNTH_REPEAT_COUNT = 5
const SYNTH_REPEAT_INTERVAL_MS = 500

/** Labels for the alarm sound dropdown UI */
export const ALARM_SOUND_LABELS: Record<AlarmSound, string> = {
  classic: 'Classic',
  bell: 'Bell',
  chime: 'Chime',
  pulse: 'Pulse',
  urgent: 'Urgent',
  arcade: 'Arcade',
  'clock-alarm': 'Clock Alarm',
  'clock-bells': 'Clock Bells',
  cricket: 'Cricket',
  emergency: 'Emergency',
  'kitchen-ding': 'Kitchen Ding',
  marimba: 'Marimba',
  'marimba-waiting': 'Marimba Waiting',
  'slot-machine': 'Slot Machine',
  'vintage-phone': 'Vintage Phone',
  'vintage-warning': 'Vintage Warning',
  bonus: 'Bonus',
}

/**
 * Audio alarm utility using Web Audio API for synth tones
 * and HTMLAudioElement for wav file playback.
 * AudioContext must be initialized from a user gesture (click, keypress)
 * before alarms can play.
 */
export class TimerAlarm {
  private audioCtx: AudioContext | null = null
  private firedThresholds = new Set<number>()
  private zeroFired = false
  private preloadedAudio: HTMLAudioElement | null = null
  private preloadedSound: string | null = null
  private pendingTimeouts: ReturnType<typeof setTimeout>[] = []
  private activeAudio: HTMLAudioElement | null = null

  initFromUserGesture(): void {
    if (this.audioCtx) {
      if (this.audioCtx.state === 'suspended') {
        this.audioCtx.resume()
      }
      return
    }
    this.audioCtx = new AudioContext()
  }

  /**
   * Preload a wav file so it plays instantly when the alarm fires.
   * Call this when the user's selected alarm sound changes.
   */
  preloadSound(sound: AlarmSound): void {
    if (isSynthTone(sound)) {
      this.preloadedAudio = null
      this.preloadedSound = null
      return
    }
    const url = WAV_SOUND_MAP[sound]
    if (this.preloadedSound === url) return
    const audio = new Audio(url)
    audio.preload = 'auto'
    this.preloadedAudio = audio
    this.preloadedSound = url
  }

  checkAndFireAlarms(
    remainingMs: number,
    thresholds: number[],
    sound: AlarmSound = 'classic',
  ): boolean {
    let fired = false

    for (const threshold of thresholds) {
      if (remainingMs <= threshold && !this.firedThresholds.has(threshold)) {
        this.firedThresholds.add(threshold)
        if (!fired) this.stopAlarm() // Stop previous alarm before new event
        this.playAlarm(sound)
        fired = true
      }
    }

    if (remainingMs <= 0 && !this.zeroFired) {
      this.zeroFired = true
      if (!fired) this.stopAlarm() // Stop previous alarm before zero event
      this.playAlarm(sound)
      const t = setTimeout(() => this.playAlarm(sound), 300)
      this.pendingTimeouts.push(t)
      fired = true
    }

    return fired
  }

  /**
   * Play the selected sound once (for previewing in settings).
   */
  playPreview(sound: AlarmSound): void {
    this.stopAlarm()
    this.playAlarm(sound)
  }

  /**
   * Cancel all in-progress alarm playback (scheduled repeats, active audio).
   */
  stopAlarm(): void {
    for (const t of this.pendingTimeouts) clearTimeout(t)
    this.pendingTimeouts = []
    if (this.activeAudio) {
      this.activeAudio.pause()
      this.activeAudio.currentTime = 0
      this.activeAudio = null
    }
  }

  resetFiredThresholds(): void {
    this.firedThresholds.clear()
    this.zeroFired = false
  }

  /**
   * Pre-mark all thresholds and zero as already fired.
   * Used when restoring timer state that's already past zero (overage)
   * to prevent alarms from re-firing on resume/reload.
   */
  markAllFired(thresholds: number[]): void {
    for (const t of thresholds) {
      this.firedThresholds.add(t)
    }
    this.zeroFired = true
  }

  private playAlarm(sound: AlarmSound): void {
    if (isSynthTone(sound)) {
      // Synth tones repeat 5 times with 0.5s interval
      const preset = SOUND_PRESETS[sound]
      for (let i = 0; i < SYNTH_REPEAT_COUNT; i++) {
        const t = setTimeout(
          () => this.playBeep(preset),
          i * SYNTH_REPEAT_INTERVAL_MS,
        )
        this.pendingTimeouts.push(t)
      }
    } else {
      this.playWav(sound)
    }
  }

  private async playWav(sound: WavSound): Promise<void> {
    const url = WAV_SOUND_MAP[sound]
    const config = WAV_PLAYBACK_CONFIG[sound]
    const repeatCount = config?.repeatCount ?? 1
    const repeatInterval = config?.repeatIntervalMs ?? 500
    const maxDuration = config?.maxDurationSec

    const playOnce = async () => {
      let audio: HTMLAudioElement
      if (this.preloadedAudio && this.preloadedSound === url) {
        audio = this.preloadedAudio
        audio.currentTime = 0
      } else {
        audio = new Audio(url)
      }

      this.activeAudio = audio

      // Stop at maxDuration if configured
      if (maxDuration) {
        const stopTimer = setTimeout(() => {
          audio.pause()
          audio.currentTime = 0
        }, maxDuration * 1000)
        this.pendingTimeouts.push(stopTimer)
        audio.addEventListener('ended', () => clearTimeout(stopTimer), {
          once: true,
        })
      }

      await audio.play().catch(() => {})
    }

    // Play first immediately, then repeat with interval
    await playOnce()
    for (let i = 1; i < repeatCount; i++) {
      const t = setTimeout(() => playOnce(), i * repeatInterval)
      this.pendingTimeouts.push(t)
    }
  }

  private playBeep(preset: SoundPreset): void {
    if (!this.audioCtx) return

    const oscillator = this.audioCtx.createOscillator()
    const gain = this.audioCtx.createGain()

    oscillator.connect(gain)
    gain.connect(this.audioCtx.destination)

    oscillator.type = preset.type
    oscillator.frequency.setValueAtTime(
      preset.frequency,
      this.audioCtx.currentTime,
    )

    gain.gain.setValueAtTime(0.3, this.audioCtx.currentTime)
    gain.gain.linearRampToValueAtTime(
      0,
      this.audioCtx.currentTime + preset.duration,
    )

    oscillator.start(this.audioCtx.currentTime)
    oscillator.stop(this.audioCtx.currentTime + preset.duration)
  }
}
